/**
 * Feature E02-F04-T08 — "Add Information" block (Stage-1 Pre-Kickoff).
 *
 * 1:1 port of the legacy SPA `../EER app/public/assets/js/project.js` Stage-1
 * right column:
 *   - render: ~899-947 (context textarea + Save Information button) and
 *     ~1166-1273 (file staging/removal, saved-links list with add/delete);
 *   - behaviour: `saveProjectChanges` (~340-390, text + staged files → ONE
 *     multipart save), `handleFileSelect`/`renderPendingFiles`/`removePendingFile`
 *     (~1174-1208), `showAddLinkDialog`/`addProjectLink` (~1241-1273, URL
 *     validated `/^https?:\/\//i`), `deleteProjectLink`/`deleteProjectFile`
 *     (~1205-1239).
 *
 * Was completely missing from the v2 UI even though every backend piece (routes,
 * hooks, contracts) was already implemented and tested — this component is the
 * missing render layer, wired to the EXISTING hooks:
 *   - `useSaveProjectChanges` (project/projectSaveApi.ts) — text + staged files,
 *     ONE multipart PATCH /projects/:id ("Save Information");
 *   - `useAddLink` / `useDeleteLink` / `useDeleteFile` (./addInformationApi.ts) —
 *     immediate (not staged) link/file mutations, matching the legacy behaviour
 *     where adding/deleting a link or a saved file is its OWN round-trip,
 *     separate from the text+pending-files "Save Information" save.
 *
 * Thin shell — the pure rules (URL validation, manual-context parsing, display
 * labels) live in `./addInformationModel.ts`.
 *
 * Rendered UNCONDITIONALLY on Stage-1 (unlike the AI-analysis brief/options,
 * which only appear once an analysis has run) — legacy parity: the "Add
 * Information" column is always visible regardless of AI-analysis state.
 */
import { useState, type ChangeEvent, type JSX, type KeyboardEvent } from 'react';
import type { ProjectFile, ProjectLink } from '@eer/shared';
import {
  formatFileSizeKb,
  isValidLinkUrl,
  linkDisplayLabel,
  linkScrapeWarning,
  readManualContextView,
} from './addInformationModel.js';
import { useAddLink, useDeleteFile, useDeleteLink, useRefetchLink } from './addInformationApi.js';
import type { AddInformationDraftApi } from './useAddInformationDraft.js';
import { useToast } from '../ui/ToastContext.js';
import { useUnsavedGuard } from '../ui/UnsavedGuard.js';
import { usePositionTypes } from '../admin/questionnaireApi.js';
import { useSavePositionType } from './stage1Api.js';
import { routeToPath, routeToState } from '../router/routes.js';

export interface AddInformationPanelProps {
  /** Project id — the save/link/file mutation target. */
  readonly projectId: number;
  /** The project's `data` blob (`manual_context.{text,links,files}` lives here). */
  readonly data: Record<string, unknown> | null | undefined;
  /**
   * Currently selected position type (projects.position_type_id) — drives the
   * "Position Type" select (E03-F02-T07). Persisted via `useSavePositionType`
   * (PATCH /projects/:id, position_type_id). Undefined/null = none selected.
   */
  readonly positionTypeId?: number | null;
  /**
   * The inbox email this project was created from (projects.source_email_id) —
   * enables the "View source email" affordance (FP-03). Null for manually-created
   * projects → the button is then hidden.
   */
  readonly sourceEmailId?: number | null;
  /**
   * PK-QA-6 — the SHARED "Add Information" draft (context text + pending files +
   * save), lifted to the parent (`PreKickoffPanel`) so the sibling "Save & Run
   * Analysis" action reads this panel's dirty state and triggers the same save.
   * This panel is now a controlled view over the draft.
   */
  readonly draft: AddInformationDraftApi;
}

const INVALID_URL_MESSAGE = 'Please enter a valid URL starting with http:// or https://';

/**
 * Navigate to the Inbox with a specific email pre-selected ("View source email").
 * Mirrors `ProjectDetailHeader.openInboxViaHistory` (E04-F02-T08): push the same
 * history entry `useRouter` expects, carrying the email id in the route state, then
 * dispatch `popstate` so `AppShell` restores the Inbox view on that email.
 */
function openSourceEmailInInbox(sourceEmailId: number): void {
  if (typeof window === 'undefined') return;
  const route = { view: 'inbox' as const, id: String(sourceEmailId) };
  window.history.pushState(routeToState(route), '', routeToPath(route));
  window.dispatchEvent(new PopStateEvent('popstate', { state: routeToState(route) }));
}

export function AddInformationPanel({
  projectId,
  data,
  positionTypeId,
  sourceEmailId,
  draft,
}: AddInformationPanelProps): JSX.Element {
  const toast = useToast();
  // E02-F03-T08 — the "View source email" jump below pushes history itself
  // (bypassing the shell's guarded `navigate`), so it asks the guard directly.
  const { guard } = useUnsavedGuard();
  const manualContext = readManualContextView(data);

  // Position Type (E03-F02-T07) — the active position_types list + the save
  // mutation (PATCH /projects/:id, position_type_id). The list endpoint is
  // admin-gated; for a recruiter it simply yields [] and the select degrades to
  // the placeholder option (no crash) — parity with the legacy Stage-1 sidebar.
  const positionTypesQuery = usePositionTypes();
  const positionMutation = useSavePositionType();
  const positionTypes = positionTypesQuery.data ?? [];

  // PK-QA-6 — the context text + pending files now live in the SHARED `draft`
  // (owned by `PreKickoffPanel` via `useAddInformationDraft`) so the sibling
  // "Save & Run Analysis" action can read this panel's dirty state and save it.

  // Add-link inline form (legacy `showAddLinkDialog` used a native prompt(); the
  // v2 UI needs a real, testable form element instead).
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const addLink = useAddLink();
  const deleteLink = useDeleteLink();
  const deleteFile = useDeleteFile();
  const refetchLink = useRefetchLink();

  function onFileSelect(event: ChangeEvent<HTMLInputElement>): void {
    draft.addFiles(Array.from(event.target.files ?? []));
    // Reset so picking the same file again still fires onChange (legacy parity).
    event.target.value = '';
  }

  function onDeleteSavedFile(index: number): void {
    deleteFile.mutate(
      { projectId, fileIndex: index },
      {
        onSuccess: () => toast.success('File deleted'),
        onError: () => toast.error('Error deleting file'),
      },
    );
  }

  /**
   * E02-F03-T08 — pass the row's URL along with its index: the index alone is a
   * position in the shared `manual_context.links` array taken from THIS render,
   * so a concurrent add/delete by another session would make it point at a
   * different link. The server treats `url` as the identity and the index only
   * as a hint (see `deleteLink`, api/services/projectMutations.ts).
   */
  function onDeleteSavedLink(index: number, url: string): void {
    deleteLink.mutate(
      { projectId, linkIndex: index, url },
      {
        onSuccess: () => toast.success('Link deleted'),
        onError: () => toast.error('Error deleting link'),
      },
    );
  }

  /**
   * PK-QA-5 — re-scrape a link whose last fetch failed / came back empty. Uses
   * the dedicated in-place refetch endpoint (NOT re-adding the URL, which would
   * duplicate the row). On success the detail-card invalidation re-reads the
   * refreshed `fetch_status`, so the badge clears when the scrape now succeeds.
   */
  function onRetryLink(index: number): void {
    refetchLink.mutate(
      { projectId, linkIndex: index },
      {
        onSuccess: () => toast.success('Link content reloaded'),
        onError: () => toast.error('Could not reload the link'),
      },
    );
  }

  function onConfirmLink(): void {
    const trimmed = linkUrl.trim();
    if (!isValidLinkUrl(trimmed)) {
      setLinkError(INVALID_URL_MESSAGE);
      return;
    }
    addLink.mutate(
      { projectId, url: trimmed },
      {
        onSuccess: () => {
          setLinkUrl('');
          setLinkError(null);
          setShowLinkForm(false);
          toast.success('Link added');
        },
        onError: () => toast.error('Error adding link'),
      },
    );
  }

  function onLinkInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      onConfirmLink();
    }
  }

  function onSaveInformation(): void {
    // PK-QA-6 — save through the shared draft (same multipart PATCH as before).
    // The draft clears its pending-files queue on success; the toast is ours.
    draft
      .save()
      .then(() => toast.success('Changes saved successfully'))
      .catch(() => toast.error('Save error'));
  }

  function onPositionTypeChange(value: string): void {
    const id = value === '' ? null : Number(value);
    positionMutation.mutate(
      { projectId, positionTypeId: id },
      {
        onSuccess: () => toast.success('Position type updated'),
        onError: () => toast.error('Could not update the position type'),
      },
    );
  }

  const hasFiles = manualContext.files.length > 0 || draft.pendingFiles.length > 0;

  return (
    <section
      data-testid="add-information-panel"
      className="space-y-4 rounded-lg border border-border p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="brand-font text-lg font-bold text-text-strong">Add Information</h3>
        {/* "View source email" (FP-03) — jump to the inbox email this project was
            created from. Hidden for manually-created projects (no source email). */}
        {sourceEmailId != null ? (
          <button
            type="button"
            data-testid="prekickoff-view-source-email"
            onClick={() => guard(() => openSourceEmailInInbox(sourceEmailId))}
            title="Open the source email in Inbox"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <i className="fa-solid fa-envelope" aria-hidden />
            View source email
          </button>
        ) : null}
      </div>

      {/* Context text (legacy manual-context-text) */}
      <div>
        <label
          htmlFor="manual-context-text"
          className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted"
        >
          Context
        </label>
        <textarea
          id="manual-context-text"
          data-testid="context-input"
          value={draft.text}
          onChange={(e) => draft.setText(e.target.value)}
          placeholder="Paste additional text, notes, or a trigger description…"
          rows={5}
          className="w-full resize-y rounded border border-border p-2 text-sm text-text focus:border-primary focus:outline-none"
        />
      </div>

      {/* Files (staged pending + saved) */}
      <div>
        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted">
          Files
        </span>
        <label
          htmlFor="add-information-file-input"
          className="inline-flex cursor-pointer items-center gap-2 rounded border border-border px-3 py-1.5 text-xs text-text-muted hover:bg-surface-muted"
        >
          <i className="fa-solid fa-paperclip" aria-hidden />
          Choose Files
        </label>
        <input
          id="add-information-file-input"
          data-testid="add-information-file-input"
          type="file"
          multiple
          className="hidden"
          onChange={onFileSelect}
        />

        {hasFiles ? (
          <ul data-testid="project-files" className="mt-2 space-y-1">
            {manualContext.files.map((file: ProjectFile, i: number) => (
              <li
                key={`saved-${i}`}
                data-testid="file-item"
                className="flex items-center justify-between gap-2 rounded border border-green-100 bg-green-50 px-2 py-1 text-xs"
              >
                <a
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-primary hover:underline"
                  title={file.name}
                >
                  <i className="fa-solid fa-check mr-1 text-green-600" aria-hidden />
                  {file.name}
                </a>
                <button
                  type="button"
                  data-testid="delete-file-btn"
                  aria-label="Delete file"
                  disabled={deleteFile.isPending}
                  onClick={() => onDeleteSavedFile(i)}
                  className="shrink-0 text-text-muted hover:text-red-500 disabled:opacity-60"
                >
                  <i className="fa-solid fa-trash-can" aria-hidden />
                </button>
              </li>
            ))}
            {draft.pendingFiles.map((file, i) => (
              <li
                key={`pending-${i}`}
                data-testid="file-item"
                className="flex items-center justify-between gap-2 rounded border border-border bg-surface-muted px-2 py-1 text-xs"
              >
                <span className="truncate text-text" title={file.name}>
                  <i className="fa-solid fa-paperclip mr-1 text-text-muted" aria-hidden />
                  {file.name}
                  <span className="ml-1 text-text-muted">({formatFileSizeKb(file.size)} KB)</span>
                </span>
                <button
                  type="button"
                  data-testid="remove-pending-file-btn"
                  aria-label="Remove file"
                  onClick={() => draft.removePendingFile(i)}
                  className="shrink-0 text-text-muted hover:text-red-500"
                >
                  <i className="fa-solid fa-times" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* Links (saved manual links + add form) */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="block text-xs font-semibold uppercase tracking-wide text-text-muted">
            Links
          </span>
          {!showLinkForm ? (
            <button
              type="button"
              data-testid="add-link-btn"
              onClick={() => setShowLinkForm(true)}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <i className="fa-solid fa-plus" aria-hidden />
              Add Link
            </button>
          ) : null}
        </div>

        {showLinkForm ? (
          <div className="mb-2 space-y-1">
            <div className="flex gap-2">
              <input
                type="url"
                data-testid="link-url-input"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  setLinkError(null);
                }}
                onKeyDown={onLinkInputKeyDown}
                placeholder="https://…"
                autoFocus
                className="flex-1 rounded border border-border px-2 py-1 text-sm text-text focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                data-testid="confirm-link-btn"
                disabled={addLink.isPending}
                onClick={onConfirmLink}
                className="shrink-0 rounded bg-primary px-3 py-1 text-xs font-semibold text-surface hover:bg-primary-hover disabled:opacity-60"
              >
                Add
              </button>
              <button
                type="button"
                data-testid="cancel-link-btn"
                onClick={() => {
                  setShowLinkForm(false);
                  setLinkUrl('');
                  setLinkError(null);
                }}
                className="shrink-0 rounded border border-border px-3 py-1 text-xs text-text-muted hover:bg-surface-muted"
              >
                Cancel
              </button>
            </div>
            {linkError ? (
              <p data-testid="link-url-error" role="alert" className="text-xs text-red-600">
                {linkError}
              </p>
            ) : null}
          </div>
        ) : null}

        {manualContext.links.length > 0 ? (
          <ul data-testid="project-links" className="space-y-1">
            {manualContext.links.map((link: ProjectLink, i: number) => {
              // PK-QA-5 — a non-'ok' fetch_status ('failed' / 'empty') means the
              // scrape yielded no usable content: warn + offer a re-scrape so the
              // user fixes it BEFORE the (paid) AI analysis, not after.
              const warning = linkScrapeWarning(link);
              return (
                <li
                  key={i}
                  data-testid="link-item"
                  data-fetch-status={link.fetch_status ?? 'ok'}
                  className={`flex flex-col gap-1 rounded border px-2 py-1 text-xs ${
                    warning ? 'border-amber-200 bg-amber-50' : 'border-blue-100 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-primary hover:underline"
                      title={link.url}
                    >
                      <i className="fa-solid fa-link mr-1 text-text-muted" aria-hidden />
                      {linkDisplayLabel(link)}
                    </a>
                    <div className="flex shrink-0 items-center gap-1">
                      {warning ? (
                        <button
                          type="button"
                          data-testid="retry-link-btn"
                          aria-label="Retry loading link"
                          disabled={refetchLink.isPending}
                          onClick={() => onRetryLink(i)}
                          className="inline-flex items-center gap-1 rounded border border-amber-300 px-1.5 py-0.5 font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-60"
                        >
                          <i
                            className={`fa-solid fa-rotate-right${
                              refetchLink.isPending ? ' fa-spin' : ''
                            }`}
                            aria-hidden
                          />
                          Retry
                        </button>
                      ) : null}
                      <button
                        type="button"
                        data-testid="delete-link-btn"
                        aria-label="Delete link"
                        disabled={deleteLink.isPending}
                        onClick={() => onDeleteSavedLink(i, link.url)}
                        className="text-text-muted hover:text-red-500 disabled:opacity-60"
                      >
                        <i className="fa-solid fa-trash-can" aria-hidden />
                      </button>
                    </div>
                  </div>
                  {warning ? (
                    <p
                      data-testid="link-fetch-warning"
                      role="alert"
                      className="flex items-center gap-1 text-[11px] text-amber-700"
                    >
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden />
                      {warning}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {/* Position Type (E03-F02-T07) — persisted to projects.position_type_id
          (PATCH /projects/:id). Ported from the legacy Stage-1 sidebar. */}
      <div data-testid="prekickoff-position-type">
        <label
          htmlFor="prekickoff-position-type-select"
          className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted"
        >
          Position Type
        </label>
        <select
          id="prekickoff-position-type-select"
          data-testid="prekickoff-position-type-select"
          value={positionTypeId == null ? '' : String(positionTypeId)}
          onChange={(e) => onPositionTypeChange(e.target.value)}
          disabled={positionMutation.isPending}
          className="w-full rounded border border-border px-2 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-60"
        >
          <option value="">— Select a position type —</option>
          {positionTypes.map((pt) => (
            <option key={pt.id} value={String(pt.id)}>
              {pt.name}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        data-testid="save-information-btn"
        disabled={draft.saving}
        onClick={onSaveInformation}
        className="w-full rounded border border-border py-2 text-sm font-semibold text-text-muted hover:bg-surface-muted disabled:opacity-60"
      >
        {draft.saving ? (
          <>
            <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
            Saving…
          </>
        ) : (
          <>
            <i className="fa-solid fa-floppy-disk mr-2" aria-hidden />
            Save Information
          </>
        )}
      </button>
    </section>
  );
}

export default AddInformationPanel;
